import {
	StatusEnum,
	useDeleteManyOrganizationUserMutation,
	useDeleteOrganizationUserMutation,
	useGetAllOrganizationUserQuery,
	useGetOrganizationUserStatsQuery,
} from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { toast } from 'sonner';
import { MoreHorizontal, Pencil, Trash2, UserPlus, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { SearchInput } from '../../components/ui/search-input';
import { SelectionBar } from '../../components/ui/selection-bar';
import { SortableTableHead } from '../../components/ui/sortable-table-head';
import { StatusBadge } from '../../components/ui/status-badge';
import { Tabs } from '../../components/ui/tabs';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { formatDate } from '../../lib/utils';
import { EmptyState } from '../../components/ui/empty-state';
import { TableSkeleton } from '../../components/ui/list-skeletons';
import { InviteUserModal } from './InviteUserModal';
import { EditUserModal } from './EditUserModal';

/* Maps the UI tab to the backend status filter (COMPLETED = active, PENDING = invited). */
function tabToStatus(tab: string): StatusEnum | undefined {
	if (tab === 'active') return StatusEnum.Completed;
	if (tab === 'pending') return StatusEnum.Pending;
	return undefined;
}

/* Status badge: Locked takes precedence, else Active / Pending. */
function UserStatusBadge({
	lockedAt,
	lastActive,
}: {
	lockedAt?: string | null;
	lastActive?: string | null;
}) {
	if (lockedAt) {
		return <StatusBadge active activeLabel="Locked" activeTone="danger" />;
	}
	return <StatusBadge active={!!lastActive} inactiveLabel="Pending" inactiveTone="warn" />;
}

/* ── Avatar tile ── */

function UserAvatar({
	firstName,
	lastName,
	avatar,
	size = 32,
}: {
	firstName: string;
	lastName: string;
	avatar?: string | null;
	size?: number;
}) {
	if (avatar) {
		return (
			<img
				src={avatar}
				alt={`${firstName} ${lastName}`}
				className="shrink-0 rounded-[var(--r-2)] object-cover"
				style={{ width: size, height: size }}
			/>
		);
	}
	const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
	return (
		<div
			className="flex shrink-0 items-center justify-center border border-line-1 bg-surface-fill font-mono font-medium tracking-[0.04em] text-text-2"
			style={{
				width: size,
				height: size,
				borderRadius: 'var(--r-2)',
				fontSize: size * 0.3,
			}}
		>
			{initials}
		</div>
	);
}

/**
 * One user row, for sizing scroll-mode batches: the 32px avatar plus TableCell's 10px vertical
 * padding and the 1px border.
 */
function userRows(): RowEstimate {
	return { rowHeight: 53 };
}

/* ════════════════════════ USERS PAGE ════════════════════════ */

export function UsersPage() {
	const { user: currentUser } = useAuth();
	const {
		search,
		setSearch,
		debouncedSearch,
		page,
		setPage,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		mode,
		setMode,
		request,
		loadMore,
		reload,
		limit,
	} = useListParams({ defaultSort: 'firstName', defaultDir: 'asc', scrollRows: userRows });
	const [tab, setTab] = useQueryParam('tab', 'all');
	const [inviteOpen, setInviteOpen] = useState(false);
	const [editTarget, setEditTarget] = useState<{
		id: string;
		firstName: string;
		lastName: string;
		jobPosition?: string | null;
		email?: string;
	} | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

	const { data, loading, refetch } = useGetAllOrganizationUserQuery({
		variables: {
			input: {
				organizationId: currentUser?.organizationId,
				pagination: request,
				search: debouncedSearch || undefined,
				order: { field: sortField, direction: sortDirection },
				status: tabToStatus(tab),
			},
		},
		fetchPolicy: 'no-cache',
	});

	// KPI counts come from a dedicated stats endpoint (global, not page-scoped).
	const { data: statsData, refetch: refetchStats } = useGetOrganizationUserStatsQuery({
		variables: { input: { organizationId: currentUser?.organizationId } },
		fetchPolicy: 'no-cache',
	});
	const stats = statsData?.getOrganizationUserStats;

	// Rows are already filtered by the active status tab server-side. This page type has `data`
	// and `pageInfo` optional (it can carry `errors` instead), so narrow it to the shape
	// usePagedList takes — memoized, since a new object every render reads as a new result.
	const userPage = data?.getAllOrganizationUser;
	const pagedUsers = useMemo(
		() =>
			userPage?.pageInfo
				? { data: userPage.data ?? [], pageInfo: userPage.pageInfo }
				: undefined,
		[userPage]
	);
	const list = usePagedList({ mode, request, limit, setPage, loadMore }, pagedUsers, loading);
	const users = list.items;

	// Surface any partial errors the list query returns in its payload.
	const responseErrors = data?.getAllOrganizationUser?.errors;
	useEffect(() => {
		if (responseErrors && responseErrors.length > 0) {
			toast.error(responseErrors[0].message);
		}
	}, [responseErrors]);

	// The signed-in user can't remove themselves from the org. currentUser.id is
	// the auth-user id, which matches each row's authenticationUser.id.
	const isSelf = (u: (typeof users)[number]) =>
		!!currentUser && u.authenticationUser?.id === currentUser.id;

	/* ── Single delete ── */
	const [deleteOrganizationUser, { loading: deleting }] = useDeleteOrganizationUserMutation();
	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			const { data } = await deleteOrganizationUser({
				variables: { input: { id: deleteTarget.id } },
			});
			if (data?.deleteOrganizationUser?.success) {
				toast.success('User removed');
				setDeleteTarget(null);
				// Step back if we just emptied a non-first page (setPage re-runs the query).
				if (mode === 'pages' && users.length <= 1 && page > 1) {
					setPage(page - 1);
				} else {
					reload(refetch);
				}
				refetchStats();
			} else {
				toast.error(data?.deleteOrganizationUser?.message || 'Failed to remove user');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove user');
		}
	};

	/* ── Selection state ── */
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

	// Selectable excludes the current user so they can't bulk-remove themselves.
	const selectableIds = useMemo(
		() => users.filter((u) => !isSelf(u)).map((u) => u.id),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[users, currentUser?.id]
	);
	const allSelected =
		selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
	const someSelected = selectableIds.some((id) => selectedIds.has(id));

	const toggleOne = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		if (allSelected) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(selectableIds));
		}
	};

	// Scrolling keeps earlier rows on screen, so in scroll mode loading more does not clear it.
	const shownPage = mode === 'pages' ? page : 0;
	useEffect(() => {
		setSelectedIds(new Set());
	}, [shownPage, mode, debouncedSearch, tab]);

	/* ── Bulk delete ── */
	const [deleteManyOrganizationUser, { loading: bulkDeleting }] =
		useDeleteManyOrganizationUserMutation();
	const handleBulkDelete = async () => {
		const count = selectedIds.size;
		try {
			const { data } = await deleteManyOrganizationUser({
				variables: { input: { ids: Array.from(selectedIds) } },
			});
			if (data?.deleteManyOrganizationUser?.success) {
				toast.success(`${count} user${count > 1 ? 's' : ''} removed`);
				setSelectedIds(new Set());
				setBulkDeleteOpen(false);
				// Step back if we just emptied a non-first page (setPage re-runs the query).
				if (mode === 'pages' && users.length - count <= 0 && page > 1) {
					setPage(page - 1);
				} else {
					reload(refetch);
				}
				refetchStats();
			} else {
				toast.error(data?.deleteManyOrganizationUser?.message || 'Failed to remove users');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove users');
		}
	};

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Users
					</h1>
					<p className="text-base text-text-3">
						Manage your organization’s team members.
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
						<UserPlus size={14} data-icon="inline-start" />
						Invite user
					</Button>
				</div>
			</div>

			{/* ── KPI strip ── */}
			<div className="mb-7 grid grid-cols-1 divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken sm:grid-cols-3 sm:divide-x sm:divide-y-0">
				{[
					{ label: 'Total users', value: stats?.total ?? '—', sub: 'All team members' },
					{
						label: 'Active',
						value: stats?.active ?? '—',
						sub: 'Signed in',
					},
					{
						label: 'Pending',
						value: stats?.pending ?? '—',
						sub: 'Invited',
					},
				].map((k) => (
					<div key={k.label} className="px-5 py-4">
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{k.label}
						</div>
						<div className="mb-1 font-mono text-2xl font-medium text-text-1">
							{k.value}
						</div>
						<div className="text-xs text-text-3">{k.sub}</div>
					</div>
				))}
			</div>

			{/* ── Tabs + filters ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All users' },
						{ value: 'active', label: 'Active' },
						{ value: 'pending', label: 'Pending' },
					]}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search users…"
						className="flex-1 lg:flex-none lg:w-[240px]"
					/>
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{/* ── Content ── */}
			{list.loading ? (
				<TableSkeleton />
			) : users.length === 0 ? (
				<EmptyState
					icon={Users}
					title="No users found"
					description={
						search
							? 'Try a different search term.'
							: tab === 'active'
								? 'No active users yet.'
								: tab === 'pending'
									? 'No pending invitations.'
									: 'Invite your first team member to get started.'
					}
				/>
			) : (
				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[700px]">
							<TableHeader>
								<TableRow className="border-line-1 hover:bg-transparent">
									<TableHead className="w-10">
										<Checkbox
											checked={allSelected}
											indeterminate={someSelected && !allSelected}
											onCheckedChange={toggleAll}
										/>
									</TableHead>
									<SortableTableHead
										sortKey="firstName"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Name
									</SortableTableHead>
									<SortableTableHead
										sortKey="email"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Email
									</SortableTableHead>
									<TableHead className="w-[120px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Position
									</TableHead>
									<TableHead className="w-[90px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Status
									</TableHead>
									<SortableTableHead
										sortKey="createdAt"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[110px] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Joined
									</SortableTableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{users.map((u) => (
									<TableRow
										key={u.id}
										data-state={selectedIds.has(u.id) ? 'selected' : undefined}
										className="cursor-pointer border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
										onClick={() =>
											setEditTarget({
												id: u.id,
												firstName: u.firstName,
												lastName: u.lastName,
												jobPosition: u.jobPosition,
												email: u.authenticationUser?.email || undefined,
											})
										}
									>
										<TableCell>
											<div onClick={(e) => e.stopPropagation()}>
												<Checkbox
													checked={selectedIds.has(u.id)}
													onCheckedChange={() => toggleOne(u.id)}
													disabled={isSelf(u)}
												/>
											</div>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-3">
												<UserAvatar
													firstName={u.firstName}
													lastName={u.lastName}
													avatar={u.avatar}
													size={32}
												/>
												<span className="text-base font-medium text-text-1">
													{u.firstName} {u.lastName}
												</span>
											</div>
										</TableCell>
										<TableCell className="text-base text-text-2">
											{u.authenticationUser?.email || '—'}
										</TableCell>
										<TableCell className="text-base text-text-2">
											{u.jobPosition || '—'}
										</TableCell>
										<TableCell>
											<UserStatusBadge
												lockedAt={u.authenticationUser?.lockedAt}
												lastActive={u.authenticationUser?.lastActive}
											/>
										</TableCell>
										<TableCell className="text-base text-text-3">
											{u.createdAt ? formatDate(u.createdAt) : '—'}
										</TableCell>
										<TableCell>
											<div className="flex justify-end">
												<DropdownMenu>
													<DropdownMenuTrigger
														onClick={(e) => e.stopPropagation()}
														aria-label="User actions"
														className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
													>
														<MoreHorizontal size={14} />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															onClick={(e) => {
																e.stopPropagation();
																setEditTarget({
																	id: u.id,
																	firstName: u.firstName,
																	lastName: u.lastName,
																	jobPosition: u.jobPosition,
																	email:
																		u.authenticationUser
																			?.email || undefined,
																});
															}}
														>
															<Pencil size={14} /> Edit
														</DropdownMenuItem>
														<DropdownMenuItem
															variant="destructive"
															disabled={isSelf(u)}
															onClick={(e) => {
																e.stopPropagation();
																setDeleteTarget({
																	id: u.id,
																	name: `${u.firstName} ${u.lastName}`,
																});
															}}
														>
															<Trash2 size={14} />{' '}
															{isSelf(u)
																? "Can't remove yourself"
																: 'Remove'}
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</Card>
			)}

			<SelectionBar
				count={selectedIds.size}
				actions={[
					{ label: 'Remove', icon: Trash2, onClick: () => setBulkDeleteOpen(true) },
				]}
				onClear={() => setSelectedIds(new Set())}
			/>

			<ListFooter list={list} />

			{/* ── Modals ── */}
			<InviteUserModal
				open={inviteOpen}
				onOpenChange={setInviteOpen}
				onSuccess={() => {
					reload(refetch);
					refetchStats();
				}}
			/>

			<EditUserModal
				open={!!editTarget}
				onOpenChange={(open) => {
					if (!open) setEditTarget(null);
				}}
				user={editTarget}
				onSuccess={() => {
					setEditTarget(null);
					reload(refetch);
				}}
			/>

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
				title="Remove user"
				description={`Are you sure you want to remove "${deleteTarget?.name}" from your organization? This action cannot be undone.`}
				confirmLabel="Remove"
				onConfirm={handleDelete}
				loading={deleting}
			/>

			<ConfirmDialog
				open={bulkDeleteOpen}
				onOpenChange={setBulkDeleteOpen}
				title="Remove users"
				description={`Are you sure you want to remove ${selectedIds.size} user${selectedIds.size > 1 ? 's' : ''} from your organization? This action cannot be undone.`}
				confirmLabel="Remove all"
				onConfirm={handleBulkDelete}
				loading={bulkDeleting}
			/>
		</div>
	);
}
